/**
 * flowTrigger.ts - Servicio para disparar flujos automáticos
 *
 * Llama al endpoint /api/flows/trigger del backend cuando ocurre un evento.
 * No bloquea la UI: se ejecuta en segundo plano (fire-and-forget).
 */

import { supabase } from './supabase'
import { logger } from './logger'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000'

interface TriggerParams {
  trigger_type: string
  trigger_config?: Record<string, any>
  student_id: string
}

/**
 * Dispara un trigger de flujo en segundo plano.
 * No lanza errores - solo los logea en consola.
 */
export async function triggerFlow(params: TriggerParams): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return

    const res = await fetch(`${BACKEND_URL}/api/flows/trigger`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        trigger_type: params.trigger_type,
        trigger_config: params.trigger_config || {},
        student_id: params.student_id
      })
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      logger.warn('[FlowTrigger] Error:', err.error || res.statusText)
      return
    }

    const result = await res.json()
    if (result.triggered > 0) {
      logger.log(`[FlowTrigger] ${result.triggered} flujo(s) ejecutado(s) para ${params.trigger_type}`)
      // Mostrar detalle de cada flujo y sus acciones
      if (result.results) {
        result.results.forEach((r: any) => {
          logger.log(`[FlowTrigger] Flujo "${r.flow_name}" → status: ${r.status}, acciones: ${r.actions_executed}`)
          if (r.actions_detail) {
            r.actions_detail.forEach((a: any) => {
              if (a.status === 'success') {
                logger.log(`  ✅ ${a.action_type}`, a)
              } else {
                logger.error(`  ❌ ${a.action_type}: ${a.error}`, a)
              }
            })
          }
        })
      }
    }
  } catch (err) {
    // Fire-and-forget: no interrumpir flujo del usuario
    logger.warn('[FlowTrigger] Error de red:', err)
  }
}

// =============================================
// Helpers para triggers comunes
// =============================================

export function triggerExerciseComplete(studentId: string, exerciseId: string) {
  triggerFlow({
    trigger_type: 'student_completes_exercise',
    trigger_config: { exercise_id: exerciseId },
    student_id: studentId
  })
}

export function triggerContentComplete(studentId: string, contentId: string) {
  triggerFlow({
    trigger_type: 'student_completes_content',
    trigger_config: { content_id: contentId },
    student_id: studentId
  })
}

export function triggerLessonComplete(studentId: string, lessonId: string) {
  triggerFlow({
    trigger_type: 'student_completes_lesson',
    trigger_config: { lesson_id: lessonId },
    student_id: studentId
  })
}

export function triggerUnitComplete(studentId: string, unitId: string) {
  triggerFlow({
    trigger_type: 'student_completes_unit',
    trigger_config: { unit_id: unitId },
    student_id: studentId
  })
}

export function triggerUnitExamPassed(studentId: string, unitId: string) {
  triggerFlow({
    trigger_type: 'student_passes_unit_exam',
    trigger_config: { unit_id: unitId },
    student_id: studentId
  })
}

export function triggerUnitExamFailed(studentId: string, unitId: string) {
  triggerFlow({
    trigger_type: 'student_fails_unit_exam',
    trigger_config: { unit_id: unitId },
    student_id: studentId
  })
}

export function triggerFinalExamPassed(studentId: string) {
  triggerFlow({
    trigger_type: 'student_passes_final_exam',
    trigger_config: {},
    student_id: studentId
  })
}

export function triggerCourseComplete(studentId: string) {
  triggerFlow({
    trigger_type: 'student_completes_course',
    trigger_config: {},
    student_id: studentId
  })
}

export function triggerFirstLogin(studentId: string) {
  triggerFlow({
    trigger_type: 'student_first_login',
    trigger_config: {},
    student_id: studentId
  })
}

export function triggerSessionStart(studentId: string) {
  triggerFlow({
    trigger_type: 'student_starts_session',
    trigger_config: {},
    student_id: studentId
  })
}

export function triggerStudentRegisters(studentId: string) {
  triggerFlow({
    trigger_type: 'student_registers',
    trigger_config: {},
    student_id: studentId
  })
}

export function triggerJoinsCohort(studentId: string, cohortId: string) {
  triggerFlow({
    trigger_type: 'student_joins_cohort',
    trigger_config: { cohort_id: cohortId },
    student_id: studentId
  })
}

export function triggerPerfectLesson(studentId: string, lessonId: string) {
  triggerFlow({
    trigger_type: 'student_perfect_lesson',
    trigger_config: { lesson_id: lessonId },
    student_id: studentId
  })
}
